import {
  useLazyGetLoanByContractNumberQuery,
  useLazyGetLoanPenaltiesQuery
} from "@/modules/loan/services";
import { LoanPenalties } from "@/modules/transaction/common/enum";
import type { ILoanRepaymentForm } from "@/modules/transaction/common/types";
import { loanRepaymentTransferValidationSchema } from "@/modules/transaction/validations";
import { CloudflareAsset, SideSheetPosition, StaticUrl, } from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { Amount, CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useMemo, useState } from "react";

import { LoanRepaymentForm } from "./LoanRepaymentForm";

export const LoanRepayment = ({ isCopy, isEdit }: IOperationPageProps) => {
  const { t, exists } = useTranslationExists();
  const isDesktop = useMediaQuery();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [isAmountDistributionOpen, setAmountDistributionOpen] = useState<boolean>(false);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [isNameUnique, setIsNameUnique] = useState<boolean>(false);

  const [getLoan, { data: loan }] = useLazyGetLoanByContractNumberQuery();

  const [getLoanPenalties, { data: loanPenalties }] = useLazyGetLoanPenaltiesQuery();

  const initialValues: ILoanRepaymentForm = {
    loanContractNumber: "",
    loanRepaymentType: "",
    transactionNumber: "",
    debitAccount: "",
    amount: "",
    isSaveAsTemplate: false,
    templateName: "",
    purpose: "",
  };

  const amountDistributionList = useMemo(() => {
    if (!loanPenalties) return [];
    const list = [] as { key: string; value: number }[];
    let total = 0;
    Object.entries(loanPenalties).forEach(([key, value]) => {
      if (value) {
        list.push({
          key,
          value,
        });
        if (key !== LoanPenalties.OverdueSubsidy && key !== LoanPenalties.Subsidy) {
          total += value;
        }
      }
    });
    setTotalAmount(Number(total.toFixed(2)));
    return list;
  }, [loanPenalties]);

  return (
    <>
      <FormContainer
        className="full-width page-container form-layout loan-repayment"
        formId="form-container"
        mode="onBlur"
        reValidateMode="onBlur"
        initialValues={initialValues}
        validationScheme={loanRepaymentTransferValidationSchema({ t, isNameUnique })}
      >
        <PageHeader>
          {!isSideBarOpen && !isAmountDistributionOpen && exists("transfer.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Loan repayment transfer info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <LoanRepaymentForm
          isEdit={isEdit}
          isCopy={isCopy}
          loan={loan}
          getLoan={getLoan}
          penalties={loanPenalties}
          getLoanPenalties={getLoanPenalties}
          setAmountDistributionOpen={setAmountDistributionOpen}
          isNameUnique={isNameUnique}
          setIsNameUnique={setIsNameUnique}
        />
      </FormContainer>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.loanRepayment.sidebarMessage")}
          </Text>
          <InfoLinks
            keys={[
              StaticUrl.CorporateTariffs,
              StaticUrl.TermsAndConditions,
            ]}
          />
        </div>
      </SideSheet>
      <SideSheet
        isWithDivider
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        withOverlay={!isDesktop}
        isOpen={isAmountDistributionOpen}
        onClose={() => setAmountDistributionOpen(false)}
        title={t("transfer.loanRepayment.amountDistribution.sidebarTitle")}
      >
        <div>
          {amountDistributionList.map((item) => (
            <div key={item.key}>
              <Text type="tertiary" className="pt-12">
                {t(`transfer.loanRepayment.${item.key}`)}
              </Text>
              <Amount
                size="standard"
                position="left"
                currency={loan?.currency}
                amount={item?.value}
                className="pt-8"
              />
              <Divider
                className="mt-12"
                color="light"
                type="primary"
                isHorizontal
              />
            </div>
          ))}
          <div className="flexbox flex-direction--column align-items--center">
            <Text type="tertiary" className="pt-12">
              {t(`transfer.loanRepayment.totalAmount`)}
            </Text>
            <Amount
              size="large"
              position="center"
              currency={loan?.currency}
              amount={totalAmount}
              className="pt-8"
            />
          </div>
        </div>
      </SideSheet>
    </>
  );
};
